<!-- src/lib/components/migration/ImportProgress.svelte -->
<!-- Step 6: Poll import progress every 2 seconds, show live status -->
<script lang="ts">
  import type { ProgressResponse } from '../../types.ts';
  import { api } from '../../api.ts';

  interface Props {
    runId: string;
    onComplete: (status: 'done' | 'error', errorMsg?: string) => void;
  }
  let { runId, onComplete }: Props = $props();

  let status = $state<string>('importing');
  let progress = $state<unknown>(null);
  let pollTimer = $state<ReturnType<typeof setInterval> | null>(null);
  let errorMsg = $state<string | undefined>(undefined);

  $effect(() => {
    // Start polling when component mounts
    pollTimer = setInterval(() => void poll(), 2000);
    void poll(); // immediate first call

    return () => {
      if (pollTimer !== null) {
        clearInterval(pollTimer);
        pollTimer = null;
      }
    };
  });

  async function poll() {
    try {
      const res = await api<ProgressResponse>(`/api/migrate/progress/${runId}`);
      status = res.status;
      progress = res.progress ?? null;

      if (res.status === 'done' || res.status === 'error') {
        if (pollTimer !== null) {
          clearInterval(pollTimer);
          pollTimer = null;
        }
        onComplete(res.status as 'done' | 'error', res.error);
      }
    } catch (e) {
      errorMsg = e instanceof Error ? e.message : 'Polling failed.';
    }
  }

  // Progress percentage if progress is a number or has a percent field
  const progressPct = $derived((() => {
    if (typeof progress === 'number') return Math.min(100, Math.max(0, progress));
    if (progress && typeof progress === 'object' && 'percent' in progress) {
      const p = (progress as { percent: number }).percent;
      return Math.min(100, Math.max(0, p));
    }
    return null;
  })());

  const statusLabel: Record<string, string> = {
    pending:   'Waiting to start…',
    fetching:  'Fetching data from source…',
    analyzing: 'Analyzing data…',
    importing: 'Importing cards and metadata…',
    done:      'Import complete!',
    error:     'Import failed.',
  };
</script>

<div class="step">
  <h2 class="step-title">Importing…</h2>
  <p class="step-hint">This may take a moment. Please keep this window open.</p>

  <div class="progress-area">
    <div class="status-row">
      {#if status !== 'done' && status !== 'error'}
        <span class="spinner" aria-hidden="true"></span>
      {:else if status === 'done'}
        <span class="check" aria-hidden="true">✓</span>
      {:else}
        <span class="x-mark" aria-hidden="true">✕</span>
      {/if}
      <span class="status-text">{statusLabel[status] ?? status}</span>
    </div>

    {#if progressPct !== null}
      <div class="progress-bar-track">
        <div class="progress-bar-fill" style="width: {progressPct}%"></div>
      </div>
      <span class="progress-pct">{Math.round(progressPct)}%</span>
    {:else}
      <div class="progress-bar-track indeterminate">
        <div class="progress-bar-fill indeterminate-fill"></div>
      </div>
    {/if}

    {#if errorMsg}
      <p class="error">{errorMsg}</p>
    {/if}
  </div>
</div>

<style>
.step { display: flex; flex-direction: column; gap: 1rem; }
.step-title { margin: 0; font-size: 1.05rem; font-weight: 600; color: var(--text-primary); }
.step-hint { margin: 0; font-size: 0.82rem; color: var(--text-muted); }

.progress-area {
  display: flex; flex-direction: column; gap: 0.75rem;
  padding: 1.25rem;
  background: rgba(255,255,255,0.02);
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 8px;
}

.status-row {
  display: flex; align-items: center; gap: 0.6rem;
}
.status-text { font-size: 0.88rem; color: var(--text-secondary); }

.check { color: var(--ab-purple-bright); font-size: 1rem; font-weight: 700; }
.x-mark { color: var(--ab-red-bright); font-size: 1rem; font-weight: 700; }

.progress-bar-track {
  height: 6px;
  background: rgba(255,255,255,0.06);
  border-radius: 3px;
  overflow: hidden;
}
.progress-bar-fill {
  height: 100%;
  background: var(--ab-purple-core);
  border-radius: 3px;
  transition: width 400ms ease;
}
.indeterminate { position: relative; }
.indeterminate-fill {
  width: 40% !important;
  background: var(--ab-purple-core);
  animation: slide 1.4s ease-in-out infinite;
}
@keyframes slide {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(350%); }
}

.progress-pct { font-size: 0.72rem; color: var(--text-muted); text-align: right; }

.error { margin: 0; font-size: 0.78rem; color: var(--ab-red-bright); }

.spinner {
  display: inline-block;
  width: 14px; height: 14px;
  border: 2px solid rgba(255,255,255,0.15);
  border-top-color: var(--ab-purple-bright);
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
  flex-shrink: 0;
}
@keyframes spin { to { transform: rotate(360deg); } }
</style>
